import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, TrendingUp, Newspaper, Briefcase, Home, Share2, BookOpen, Facebook, Instagram, Youtube } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.png";

const navItems = [
  { label: "Home", path: "/", icon: Home },
  { label: "IPOs", path: "/ipos", icon: TrendingUp },
  { label: "IPO GMP", path: "/ipo-gmp", icon: TrendingUp },
  { label: "News", path: "/news", icon: Newspaper },
  { label: "Jobs", path: "/jobs", icon: Briefcase },
  { label: "Learning", path: "/learning", icon: BookOpen },
  { label: "Refer & Earn", path: "/referral", icon: Share2 },
];

const socialLinks = [
  { name: "Instagram", icon: Instagram, url: "https://www.instagram.com/dkonnects_ai/", color: "hover:text-pink-600" },
  { name: "Facebook", icon: Facebook, url: "https://www.facebook.com/profile.php?id=100077077226047&sk=about", color: "hover:text-blue-600" },
  { name: "YouTube", icon: Youtube, url: "https://www.youtube.com/@dkonnects", color: "hover:text-red-600" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="bg-slate-900 text-white border-b border-slate-800">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          
          <Link to="/" className="flex items-center gap-2.5">
            <img src={logo} alt="dkonnects logo" className="w-10 h-10 rounded-full object-cover" />
            <div className="leading-tight">
              <span className="font-display font-bold text-lg text-accent">Dashboard</span>
              <span className="block text-[10px] text-blue-400 font-semibold -mt-0.5 tracking-wide">
                Powered by dkonnects
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    active
                      ? "bg-blue-600 text-white"
                      : "text-gray-300 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* Social Media Icons (No Extra Space Now) */}
            <div className="flex items-center gap-3 ml-2">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-gray-400 hover:text-white transition-colors ${social.color}`}
                  title={social.name}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-slate-800"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-slate-900 border-t border-slate-800 overflow-hidden"
          >
            <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                      active
                        ? "bg-blue-600 text-white"
                        : "text-gray-300 hover:bg-slate-800 hover:text-white"
                    }`}
                  >
                    <Icon size={18} />
                    {item.label}
                  </Link>
                );
              })}

              {/* Mobile Social */}
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-800">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-400 hover:text-white transition-colors ${social.color}`}
                    title={social.name}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
